import { useInfiniteQuery } from "@tanstack/react-query";
import { CircleAlert } from "lucide-react";
import { listCodeLibraryComparisons, type ComparisonJob } from "@/api/code_library_compare";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { COMPARISON_HISTORY_QUERY_KEY, useCodeLibraryCompare } from "@/context/CodeLibraryCompareContext";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";
import { cn } from "@/lib/utils";

const HISTORY_POLL_INTERVAL_MS = 5000;

const isRunning = (comparison: ComparisonJob) =>
  comparison.status === "pending" || comparison.status === "processing";

function FailedMark({ comparison }: { comparison: ComparisonJob }) {
  if (comparison.status !== "failed") return null;
  return (
    <>
      <CircleAlert className="h-3.5 w-3.5 shrink-0 text-destructive" aria-hidden />
      <span className="sr-only">Could not be completed</span>
    </>
  );
}

/** The signed-in user's Code Library comparisons, newest first. */
export function CodeLibraryComparisonsList() {
  const { activeComparisonId, openComparison } = useCodeLibraryCompare();
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: COMPARISON_HISTORY_QUERY_KEY,
    queryFn: ({ pageParam }) => listCodeLibraryComparisons(pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.current_page < lastPage.pagination.total_pages
        ? lastPage.pagination.current_page + 1
        : undefined,
    // Picks up a running comparison's failure so its row can show it.
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) => page.comparisons.some(isRunning)) ? HISTORY_POLL_INTERVAL_MS : false,
  });
  const comparisons = data?.pages.flatMap((page) => page.comparisons) ?? [];

  if (isLoading) {
    return (
      <div className="space-y-2 px-2 py-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-[200px]" />
        ))}
      </div>
    );
  }

  return (
    <div className="w-full max-w-[236px] py-2">
      {isError && (
        <p className="px-3 text-xs text-destructive" role="alert">
          Your comparisons could not be loaded.
        </p>
      )}
      {!isError && comparisons.length === 0 && (
        <p className="px-3 text-xs italic text-muted-foreground">No comparisons yet</p>
      )}
      <ul aria-label="Comparison history" className="space-y-0.5">
        {comparisons.map((comparison) => {
          const isActive = comparison.id === activeComparisonId;
          const pair = `${formatMunicipalityLabel(comparison.home_municipality)} & ${formatMunicipalityLabel(comparison.comparison_municipality)}`;
          return (
            <li key={comparison.id}>
              <Button
                variant={isActive ? "secondary" : "ghost"}
                className={cn("h-auto w-full justify-start px-3 py-1.5 text-left", isActive && "font-medium")}
                aria-current={isActive ? "true" : undefined}
                title={`${comparison.prompt}\n${pair}`}
                onClick={() => openComparison(comparison.id)}
              >
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate text-sm">{comparison.prompt}</span>
                  <span className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                    <span className="truncate">{pair}</span>
                    <FailedMark comparison={comparison} />
                  </span>
                </span>
              </Button>
            </li>
          );
        })}
      </ul>
      {hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-1 w-full justify-start px-3 text-xs text-muted-foreground"
          disabled={isFetchingNextPage}
          onClick={() => void fetchNextPage()}
        >
          {isFetchingNextPage ? "Loading..." : "Show older comparisons"}
        </Button>
      )}
    </div>
  );
}
